/**
 * Products sold by weight: the Admin sets a base weight and its price, plus the weights
 * customers may choose. Prices are proportional to the base and rounded to the centime.
 */

export interface WeightedProduct {
  priceCents: number;
  baseWeightGrams: number | null;
  weightOptionsGrams: number[];
}

/**
 * Selling type chosen by the Admin: sold by weight when a base weight is set, otherwise by unit.
 * Weight options are optional: without them the product is sold in multiples of its base weight.
 */
export function isSoldByWeight(p: WeightedProduct): p is WeightedProduct & { baseWeightGrams: number } {
  return !!p.baseWeightGrams && p.baseWeightGrams > 0;
}

/** Price of `weightGrams`, proportional to the base weight and price. */
export function weightPriceCents(priceCents: number, baseWeightGrams: number, weightGrams: number): number {
  return Math.round((priceCents * weightGrams) / baseWeightGrams);
}

/** "250 g", "1 kg", "1,5 kg" (fr) / "250 غ", "1.5 كلغ" (ar). */
export function formatWeight(grams: number, locale: "fr" | "ar" = "fr"): string {
  if (grams < 1000) return locale === "ar" ? `${grams} غ` : `${grams} g`;
  const kg = String(Math.round(grams / 10) / 100);
  return locale === "ar" ? `${kg} كلغ` : `${kg.replace(".", ",")} kg`;
}

/** Product name showing the chosen weight: "Tomates (1 kg)" → "Tomates (500 g)". */
export function nameWithWeight(name: string, grams: number, locale: "fr" | "ar" = "fr"): string {
  const label = `(${formatWeight(grams, locale)})`;
  return /\([^()]*\)\s*$/.test(name) ? name.replace(/\([^()]*\)\s*$/, label) : `${name} ${label}`;
}
